/* eslint-disable no-debugger */
import React, { useContext, useEffect, useMemo, useState } from "react";
import "./RecFishing.scss";
import { MapContext } from "../../../context/MapContext";
import GeoSelector from "../../GeoSelector/GeoSelector";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import QueryTask from "@arcgis/core/tasks/QueryTask";
import Query from "@arcgis/core/tasks/support/Query";
import GeoScale from "../../GeoScale/GeoScale";
import {
  RecFishingConfig,
  refLayersToInclude,
  ReferenceConfig,
} from "./RecFishingConfig";
import useQueryString from "../../../hooks/useQueryString";
import ReferenceLayers from "../../ReferenceLayers/ReferenceLayers";
import RecFishingStats from "./RecFishingStats/RecFishingStats";
import utils from "../../../helpers/utils";
import References from "../../References/References";

function RecFishing() {
  const [mapObject] = useContext(MapContext);
  const [selectedGeometry, setSelectedGeometry] = useQueryString(
    "selectedGeometry",
    "CROP Countries"
  );
  const [isGeoScaled, setIsGeoScaled] = useQueryString("isGeoScaled", false);
  const [stats, setStats] = useState({});
  const [refLayersEnabled, setRefLayersEnabled] = useQueryString(
    "refLayers",
    []
  );

  const geoSelections = [
    "CROP Countries",
    "Dominica",
    "Grenada",
    "Saint Kitts and Nevis",
    "Saint Lucia",
    "Saint Vincent and the Grenadines",
  ];

  /**
   * Every time this route is accessed, make sure the EEZ layer reflects the correct boundaries
   */
  useEffect(() => {
    const eez = mapObject?.map.findLayerById("eez");
    const countries = RecFishingConfig.data.map((c) => c.name);

    if (eez) {
      eez.definitionExpression = `Dissolve in (${countries
        .map((c) => "'" + utils.getCountryForEEZ(c) + "'")
        .join(",")})`;
    }
  }, [mapObject]);

  /**
   * Function that looks up layer object from config file to use based on current
   * app selections (selected geometry)
   */
  const getLayerForSelections = (geometry, isScaled) => {
    const dataSet = RecFishingConfig.data;
    if (isScaled) {
      return dataSet.find((country) => country.name === geometry);
    }
    return dataSet.find((country) => country.name === "CROP Countries");
  };

  /**
   * Only one model layer is active at a time. Memoize it and update when user selections change
   */
  const layer = useMemo(() => {
    const dataLayer = getLayerForSelections(selectedGeometry, isGeoScaled);
    if (dataLayer) {
      return new FeatureLayer({
        url: dataLayer.url,
        title: dataLayer.name + " Recreational Fishing",
      });
    }

    return null;
  }, [selectedGeometry, isGeoScaled]);

  /**
   * When the memoized layer object updates, we need to remove the current layer
   */
  useEffect(() => {
    if (layer) {
      mapObject?.map.remove(layer);
    }
  }, [layer, mapObject?.map]);

  /**
   * Second effect declared to add the new layer. Runs after first efect that removes. Has callback for removing on component unmount
   */
  useEffect(() => {
    if (layer) {
      mapObject?.map.add(layer);

      return () => {
        mapObject?.map.remove(layer);
      };
    }
  }, [mapObject?.map, layer]);

  /**
   * On component mount, fetch the stats table and prepare a query tasks to execute queries on
   */
  const queryTable = useMemo(() => {
    const crtTable = RecFishingConfig.data.find(
      (crt) => crt.name === "Statistics Table"
    );
    return new QueryTask({ url: crtTable.url });
  }, []);

  /**
   * Fetch the stats for the currrent app and set them in state.
   */
  useEffect(() => {
    const query = new Query();
    query.returnGeometry = false;
    query.outFields = ["*"];
    query.where = "App = 'Recreational Fishing'";

    queryTable.execute(query).then((results) => {
      const newStats = {};
      results.features.forEach((feature) => {
        newStats[feature.attributes.Statistic] = feature.attributes;
      });
      setStats(newStats);
    });
  }, [queryTable, setStats]);

  /**
   * Simple handler for when the user selects a new geometry. Update in state
   */
  const handleGeometryChange = (geometry) => {
    setSelectedGeometry(geometry);
    //auto-scale data if anything is selected
    setIsGeoScaled(geometry !== "CROP Countries");
  };

  /**
   * Simple handler for when the user toggle the geo scaler. Update in state
   */
  const onGeoScaleToggle = () => {
    setIsGeoScaled(!isGeoScaled);
  };

  /**
   * Toggles the visibility of a reference layer by adding/removing from an array that tracks currently visible ref layers
   */
  const handleRefLayerToggle = (layer) => {
    if (refLayersEnabled.includes(layer)) {
      //turn off
      setRefLayersEnabled([]);
    } else {
      //turn on
      setRefLayersEnabled([layer]);
    }
  };

  return (
    <div className="recFishing">
      <p className="instructions">
        Explore the intensity, distribution, and values of recreational charter
        fishing in five Eastern Caribbean countries.
      </p>
      <div className="geoSelect">
        <GeoSelector
          selectedGeometry={selectedGeometry}
          handleOnChange={handleGeometryChange}
          selections={geoSelections}
        />
        <GeoScale geoScaled={isGeoScaled} onToggle={onGeoScaleToggle} />
      </div>
      <div className="quickStats">
        <h3>Quick Stats for {selectedGeometry}</h3>
        <RecFishingStats stats={stats} selectedGeometry={selectedGeometry} />
      </div>
      <div className="otherMaps">
        <div className="otherHeader">
          <h3>Additional Map Layers:</h3>
        </div>

        <ReferenceLayers
          layersToInclude={refLayersToInclude}
          layersEnabled={refLayersEnabled}
          isRadio
          handleLayerToggle={handleRefLayerToggle}
        />
      </div>
      {ReferenceConfig && (
        <div className="references-wrapper">
          <h3>References</h3>
          <References refs={ReferenceConfig} />
        </div>
      )}
    </div>
  );
}

export default RecFishing;
